/**
 * Pure helpers for the quick-search recent-visits feature.
 *
 * No React, no MUI, no Backstage framework imports — this file must be
 * importable from a plain Node test environment and from `recentSearches.ts`
 * without pulling in any browser-only or React-specific modules.
 *
 * StorageApi.set requires T extends JsonValue, which does NOT allow undefined
 * values. Optional fields on RecentVisit must therefore be omitted entirely
 * when absent. makeVisit() enforces this via conditional spreads.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** StorageApi bucket name for all quick-search state. */
export const BUCKET = 'quick-search';

/** Key within the bucket where the MRU visits array is stored. */
export const VISITS_KEY = 'recent-visits';

/** Maximum number of recent visits to retain. Oldest entries are dropped. */
export const MAX_VISITS = 8;

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * A single entry in the recent-visits list.
 *
 * `location` is the *resolved* navigation path — the post-resolveLocation
 * value (e.g. `/create/templates/default/my-template` for a catalog template,
 * not the raw `/catalog/default/template/my-template`). This means clicking
 * the entry navigates to the right place without re-running any URL rewrite.
 */
export type RecentVisit = {
  title: string;
  location: string;
  kind?: string; // e.g. "Component", "API", "Template"
  sourceLabel?: string; // e.g. "Catalog", "Docs"
  visitedAt: number; // Date.now() at the moment of activation
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build a JsonValue-safe RecentVisit.
 *
 * Optional fields (`kind`, `sourceLabel`) are omitted from the returned object
 * when not provided, rather than being set to `undefined`. This keeps the
 * stored JSON valid under StorageApi's `T extends JsonValue` constraint.
 */
export function makeVisit(input: {
  title: string;
  location: string;
  kind?: string;
  sourceLabel?: string;
  visitedAt: number;
}): RecentVisit {
  return {
    title: input.title,
    location: input.location,
    visitedAt: input.visitedAt,
    ...(input.kind !== undefined ? { kind: input.kind } : {}),
    ...(input.sourceLabel !== undefined
      ? { sourceLabel: input.sourceLabel }
      : {}),
  };
}

/**
 * Prepend a visit to the MRU list, deduping by `location` and capping at
 * MAX_VISITS.
 *
 * If an entry with the same `location` already exists it is removed first so
 * the new entry (with updated title/timestamp) lands at the front without
 * creating a duplicate.
 *
 * Pure — does not mutate `current`.
 */
export function pushVisit(
  current: RecentVisit[],
  v: RecentVisit,
): RecentVisit[] {
  const deduped = current.filter(entry => entry.location !== v.location);
  return [v, ...deduped].slice(0, MAX_VISITS);
}

/**
 * Remove the entry with the given `location` from the list.
 *
 * No-op when no entry with that location exists.
 *
 * Pure — does not mutate `current`.
 */
export function removeVisit(
  current: RecentVisit[],
  location: string,
): RecentVisit[] {
  return current.filter(entry => entry.location !== location);
}
